// TCIO Prescreen — Master Redesign (spec ratified 2026-09-01).
// PART A: Prescreen Home / Work Queue (this build). PART B: client workspace (carried
// as-is this step; rebuilt in Steps 2-8). One dashboard payload drives all of Part A
// so facility scoping updates KPIs/priority/tables atomically (spec A3).
import { useState, useEffect, useRef, useMemo } from 'react';
import jsPDF from 'jspdf';
import { tokens as tk } from './theme';
import {
  UserRound, FileText, Droplets, History, Brain, HeartPulse, Activity,
  ClipboardList, Briefcase, UsersRound, Sparkles, CircleHelp, ClipboardCheck,
  PhoneCall, ChevronDown, ChevronUp, ChevronRight, Search, Mic, Square, Send,
  Download, Mail, CheckCircle2, AlertTriangle, Pencil, X, FolderOpen, FilePen,
  Plus, MoreVertical, Users, FileCheck2, Timer, CalendarCheck, Flame,
} from 'lucide-react';

const API = 'https://tcio-api.onrender.com';
const DOT = '\u00B7';
const DASH = '\u2014';
const ELLIP = '\u2026';
const CHECK = '\u2713';
const ARROW = '\u2192';
const IC = { size: 17, strokeWidth: 1.75 };
const ICS = { size: 14, strokeWidth: 1.75 };

const SUBSTANCES_LIST = ['Alcohol', 'Heroin', 'Fentanyl', 'Opiates', 'Methamphetamine', 'Cocaine', 'Crack Cocaine', 'Benzodiazepines', 'Stimulants (Adderall/Rx)', 'Kratom', 'Marijuana', 'Psychedelics (Mushrooms/LSD)', 'MDMA/Ecstasy', 'Polysubstance', 'Other'];
const ADMIN_OPTIONS = ['IV', 'Insufflated (snort)', 'Smoked', 'Oral', 'Other'];
const LOC_OPTIONS = ['DTX', 'RTC', 'PHP', 'IOP', 'OP'];

// Pristine form state (cc697a24 no-carryover doctrine).
const BLANK_FORM = {
    assessment_title: '', presenting_problem: '', source: '',
    ssn: '', dob: '', primary_phone: '', primary_email: '',
    mailing_street: '', city: '', mailing_state: '', mailing_zip: '',
    pronouns: '', sex: '', marital_status: '', race: '', ethnicity: '', dietary: '',
    treatment_history: [], substances: [],
    mh_diagnosis: '', suicidal_homicidal_ideation: '', si_hi_details: '', suicide_attempts: '', si_attempt_details: '', other_addictive_behaviors: '',
    ever_overdosed: '', overdose_details: '', medical_conditions: '', allergies: '', current_medications: [],
    contributing_factors: '', living_situation: '', withdrawal_symptoms: '', withdrawal_details: '', seizure_history: '', seizure_details: '', eating_disorder: '', eating_disorder_details: '',
    how_support_use: '', aa_na: '', who_initiated: '', family_history: '',
    sexual_crime_conviction: '', arson_conviction: '', pending_charges: '', charges_details: '',
    on_probation: '', probation_details: '', legal_history: '',
    currently_employed: '', employer_knows: '', insurance_through_employer: '', needs_fmla: '',
    related_contacts: [],
    mh_symptoms: '', mh_current_struggles: '', adl_skills: '', mania_psychosis: '', past_si_hi: '', self_harm: '', on_disability: '',
    recommend_dtx: false, recommend_rtc: false, recommend_php: false, recommend_iop: false, recommend_op: false,
    recommendation_to_admit: null, recommendation_notes: '',
  };

// COMPLETENESS ENGINE (R-rulings; mirrors the server's REQ list in /dashboard).
const REQUIRED_FIELDS = [
  ['dob', 'Date of birth'],
  ['primary_phone', 'Primary phone'],
  ['presenting_problem', 'Presenting problem'],
  ['substances', 'At least one substance'],
  ['suicidal_homicidal_ideation', 'Current SI/HI'],
  ['withdrawal_symptoms', 'Withdrawal symptoms'],
  ['seizure_history', 'Seizure history'],
  ['allergies', 'Allergies (or "denies")'],
  ['living_situation', 'Living situation'],
  ['recommendation_to_admit', 'Recommendation to admit'],
];
const isAnswered = (v) => {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.some(r => (r && typeof r === 'object') ? Boolean(r.substance || r.medication || r.facility_name || r.levels_of_care || r.primary_phone || r.relationship) : Boolean(r));
  return true;
};
const missingRequired = (form) => REQUIRED_FIELDS.filter(([k]) => !isAnswered(form[k]));

const SECTION_DEFS = [
  { id: 'basic',          title: 'Basic Information',        icon: FileText,     keys: ['presenting_problem','source'] },
  { id: 'facesheet',      title: 'Face Sheet',               icon: UserRound,    keys: ['dob','primary_phone','primary_email','mailing_street','city','mailing_state','mailing_zip','sex','marital_status','race','ethnicity'] },
  { id: 'treatment',      title: 'Treatment History',        icon: History,      keys: ['treatment_history'] },
  { id: 'substances',     title: 'Substance Use',            icon: Droplets,     keys: ['substances'] },
  { id: 'mentalhealth',   title: 'Mental Health',            icon: Brain,        keys: ['mh_diagnosis','suicidal_homicidal_ideation','suicide_attempts'] },
  { id: 'medical',        title: 'Medical / Health',         icon: HeartPulse,   keys: ['ever_overdosed','medical_conditions','seizure_history','eating_disorder','allergies'] },
  { id: 'functional',     title: 'Functional Impact',        icon: Activity,     keys: ['living_situation','withdrawal_symptoms','aa_na','who_initiated'] },
  { id: 'legal',          title: 'Legal History',            icon: ClipboardList,keys: ['sexual_crime_conviction','arson_conviction','pending_charges','on_probation'] },
  { id: 'employment',     title: 'Employment',               icon: Briefcase,    keys: ['currently_employed','employer_knows','insurance_through_employer','needs_fmla'] },
  { id: 'contacts',       title: 'Related Contacts',         icon: UsersRound,   keys: ['related_contacts'] },
  { id: 'mhtrack',        title: 'MH Track (Optional)',      icon: Brain,        keys: [] },
  { id: 'recommendation', title: 'Recommendation to Admit',  icon: ClipboardCheck, keys: ['recommendation_to_admit'] },
];
const sectionStatus = (def, form) => {
  const total = def.keys.length;
  const done = def.keys.filter(k => isAnswered(form[k])).length;
  const attn = [];
  if (def.id === 'mentalhealth' && form.suicidal_homicidal_ideation === 'Yes') attn.push('SI/HI reported');
  if (def.id === 'mentalhealth' && form.suicide_attempts === 'Yes') attn.push('Suicide attempt history');
  if (def.id === 'medical' && form.seizure_history === 'Yes') attn.push('Seizure history');
  if (def.id === 'medical' && form.ever_overdosed === 'Yes') attn.push('Overdose history');
  if (def.id === 'medical' && form.eating_disorder === 'Yes') attn.push('Eating disorder');
  if (def.id === 'functional' && form.withdrawal_symptoms === 'Yes') attn.push('Withdrawal symptoms');
  return { total, done, complete: total > 0 && done === total, attn };
};
const sectionFacts = (def, form) => {
  try {
    if (def.id === 'substances' && form.substances.length) {
      return form.substances.slice(0, 3).map(su => [su.substance === 'Other' && su.details ? su.details : su.substance, su.frequency].filter(Boolean).join(' ')).filter(Boolean).join(' ' + DOT + ' ');
    }
    if (def.id === 'treatment' && form.treatment_history.length) {
      const last = form.treatment_history.map(t => t.discharge_date || t.admission_date).filter(Boolean).sort().pop();
      return `${form.treatment_history.length} prior episode${form.treatment_history.length === 1 ? '' : 's'}${last ? ` ${DOT} last ${String(last).slice(0, 4)}` : ''}`;
    }
    if (def.id === 'mentalhealth' && form.mh_diagnosis) return String(form.mh_diagnosis).slice(0, 60);
    if (def.id === 'medical') {
      const bits = [];
      if (form.seizure_history === 'Yes') bits.push('Seizure reported');
      if (form.ever_overdosed === 'Yes') bits.push('Overdose reported');
      if (form.current_medications.length) bits.push(`${form.current_medications.length} med${form.current_medications.length === 1 ? '' : 's'}`);
      return bits.join(' ' + DOT + ' ');
    }
    if (def.id === 'functional' && form.living_situation) return String(form.living_situation).slice(0, 50);
    if (def.id === 'contacts' && form.related_contacts.length) {
      const c = form.related_contacts[0];
      return [c.name, c.relationship ? `(${c.relationship})` : ''].filter(Boolean).join(' ');
    }
    if (def.id === 'legal') {
      const any = ['sexual_crime_conviction','arson_conviction','pending_charges','on_probation'].some(k => form[k] === 'Yes');
      if (form.legal_history || any) return any ? 'Legal issues reported' : String(form.legal_history).slice(0, 50);
      const answered = ['sexual_crime_conviction','arson_conviction','pending_charges','on_probation'].every(k => isAnswered(form[k]));
      return answered ? 'No current legal issues reported' : '';
    }
    if (def.id === 'recommendation') {
      if (form.recommendation_to_admit === true) return 'Recommend admit' + (LOC_OPTIONS.filter((_, i) => form[['recommend_dtx','recommend_rtc','recommend_php','recommend_iop','recommend_op'][i]]).length ? ' ' + DOT + ' ' + LOC_OPTIONS.filter((_, i) => form[['recommend_dtx','recommend_rtc','recommend_php','recommend_iop','recommend_op'][i]]).join('/') : '');
      if (form.recommendation_to_admit === false) return 'Do not admit';
    }
  } catch (e) { /* facts are cosmetic - never break the form */ }
  return '';
};

// ── PART A HELPERS ──
const timeAgo = (ts) => {
  if (!ts) return null;
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (isNaN(m) || m < 0) return null;
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  return `${dd}d ago`;
};
// Explainable priority only (spec A5): every level maps to existing workflow signals.
// vob_returned deliberately excluded - present on ~99% of pending admits, differentiates nothing.
const priorityOf = (r) => {
  const reasons = [];
  let score = 0;
  if (r.crisis_flag) { score += 100; reasons.push('Crisis flag'); }
  if (r.prescreen_status === 'pending_dr_approval') { score += 60; reasons.push('Awaiting Dr approval'); }
  if (r.prescreen_id && r.required_missing === 0 && r.prescreen_status === 'draft') { score += 50; reasons.push('Ready for review'); }
  if (r.prescreen_requested_at) { score += 40; reasons.push('Prescreen requested'); }
  if (r.prescreen_id && r.required_missing != null && r.required_missing > 0 && r.required_missing <= 3) { score += 25; reasons.push(`${r.required_missing} required left`); }
  if ((r.calls_complete || 0) >= 2 && !r.prescreen_id) { score += 20; reasons.push(`${r.calls_complete} calls, no prescreen`); }
  const level = score >= 60 ? 'HIGH' : score >= 20 ? 'MEDIUM' : null;
  return { score, level, reasons };
};
const prescreenState = (r) => {
  if (!r.prescreen_id) return { label: 'Not Started', pct: null, kind: 'none' };
  if (r.prescreen_status === 'pending_dr_approval') return { label: 'Ready for Review', pct: null, kind: 'review' };
  if (r.required_missing === 0 && r.prescreen_status === 'draft') return { label: 'Ready for Review', pct: null, kind: 'review' };
  const pct = r.required_missing != null ? Math.round(100 * (r.required_total - r.required_missing) / r.required_total) : null;
  return { label: pct != null ? pct + '%' : 'In Progress', pct, kind: 'progress' };
};

const s = {
  page: { padding: '20px 24px 40px', maxWidth: '1360px', margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh' },
  pageTitle: { fontSize: '22px', fontWeight: 700, color: tk.ink, margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 8 },
  pageSub: { fontSize: '13px', color: tk.textSecondary, marginBottom: '16px' },
  card: { background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow },
  select: { padding: '8px 12px', fontSize: '13px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, outline: 'none', background: tk.surface, color: tk.ink, fontFamily: 'inherit' },
  input: { padding: '8px 12px', fontSize: '13px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, outline: 'none', color: tk.ink, fontFamily: 'inherit' },
  textarea: { padding: '8px 12px', fontSize: '13px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, outline: 'none', color: tk.ink, resize: 'vertical', minHeight: '70px', fontFamily: 'inherit' },
  selectField: { padding: '8px 12px', fontSize: '13px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, outline: 'none', background: tk.surface, color: tk.ink, fontFamily: 'inherit' },
  label: { fontSize: '10.5px', fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '12px' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldFull: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  radioGroup: { display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: tk.ink, cursor: 'pointer' },
  repeatRow: { background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: '14px', marginBottom: '10px' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: tk.aiSoft, color: tk.ai, border: `1px solid ${tk.ai}`, borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, marginTop: '6px', fontFamily: 'inherit' },
  removeBtn: { padding: '4px 10px', background: tk.dangerBg, color: tk.danger, border: `1px solid ${tk.danger}`, borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: '11.5px', fontWeight: 700, float: 'right', fontFamily: 'inherit' },
  recBox: { border: `1.5px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, padding: '14px 18px', marginBottom: '10px' },
  recBoxActive: { border: `1.5px solid ${tk.success}`, borderRadius: tk.radiusCard, padding: '14px 18px', marginBottom: '10px', background: tk.successBg },
  recTitle: { fontSize: '13.5px', fontWeight: 700, color: tk.ink, marginBottom: '3px' },
  recCriteria: { fontSize: '12px', color: tk.textSecondary, lineHeight: 1.6 },
  successMsg: { fontSize: '13px', color: tk.success, fontWeight: 600, alignSelf: 'center' },
  errorMsg: { fontSize: '13px', color: tk.danger, fontWeight: 600, alignSelf: 'center' },
  btn: (bg, fg, br) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: bg, color: fg, border: br ? `1px solid ${br}` : 'none', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'inherit' }),
  // Part A dense-table language
  th: { fontSize: '10.5px', fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tk.borderSoft}` },
  td: { fontSize: '13px', color: tk.ink, padding: '9px 12px', borderBottom: `1px solid ${tk.borderSoft}`, verticalAlign: 'middle' },
  chip: (bg, fg, br) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 8, background: bg, color: fg, border: br ? `1px solid ${br}` : 'none', whiteSpace: 'nowrap' }),
};

function YesNo({ value, onChange }) {
  return (
    <div style={s.radioGroup}>
      {['Yes', 'No', 'Unknown'].map(opt => (
        <label key={opt} style={s.radioLabel}>
          <input type="radio" checked={value === opt} onChange={() => onChange(opt)} />
          {opt}
        </label>
      ))}
    </div>
  );
}
function StatusChip({ st }) {
  if (st.attn.length) return <span style={s.chip(tk.warningBg, tk.warning, tk.warning)}>ATTENTION</span>;
  if (st.total === 0) return null;
  if (st.complete) return <span style={s.chip(tk.successBg, tk.success)}>COMPLETE {CHECK}</span>;
  return <span style={{ ...s.chip(tk.surfaceSoft, tk.textSecondary, tk.borderSoft), fontVariantNumeric: 'tabular-nums' }}>{st.done} / {st.total}</span>;
}
function PSection({ def, form, openSections, toggleSection, saveAndNext, children }) {
  const st = sectionStatus(def, form);
  const facts = sectionFacts(def, form);
  const Icon = def.icon;
  return (
    <div style={{ ...s.card, marginBottom: 10, overflow: 'hidden', borderLeft: st.attn.length ? `3px solid ${tk.warning}` : undefined }}>
      <div onClick={() => toggleSection(def.id)} style={{ padding: '12px 18px', background: st.complete && !st.attn.length ? tk.surface : tk.surfaceSoft, borderBottom: openSections[def.id] ? `1px solid ${tk.borderSoft}` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, flexWrap: 'wrap' }}>
          <Icon {...IC} color={st.attn.length ? tk.warning : st.complete ? tk.success : tk.blue600} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{def.title}</span>
          <StatusChip st={st} />
          {(st.attn.length ? st.attn.join(' ' + DOT + ' ') : facts) && (
            <span style={{ fontSize: 12, color: st.attn.length ? tk.warning : tk.textMuted, fontWeight: st.attn.length ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 380 }}>
              {st.attn.length ? st.attn.join(' ' + DOT + ' ') : facts}
            </span>
          )}
        </div>
        {openSections[def.id] ? <ChevronUp {...ICS} color={tk.textMuted} /> : <ChevronDown {...ICS} color={tk.textMuted} />}
      </div>
      {openSections[def.id] && (
        <div style={{ padding: '18px 22px' }}>
          {children}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tk.borderSoft}` }}>
            <button onClick={() => saveAndNext(def.id)} style={s.btn(tk.aiSoft, tk.ai, tk.ai)}>Save & Next {ARROW}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniBar({ pct }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 90 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums', minWidth: 32 }}>{pct}%</span>
      <div style={{ flex: 1, height: 5, background: tk.surfaceSoft, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: pct >= 100 ? tk.success : tk.ai, borderRadius: 3 }} />
      </div>
    </div>
  );
}
const Avatar = ({ first, last }) => (
  <span style={{ width: 32, height: 32, borderRadius: '50%', background: tk.aiSoft, color: tk.blue700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
    {(first || '?')[0]}{(last || '')[0] || ''}
  </span>
);

const emptySubstance = () => ({ substance: '', details: '', amount_24h: '', frequency: '', administered: '', age_first_use: '', last_use: '', is_relapse: '', relapse_length: '', notes: '' });
const emptyTreatment = () => ({ facility_name: '', admission_date: '', completed: '', discharge_date: '', levels_of_care: '', location: '' });
const emptyContact = () => ({ name: '', relationship: '', primary_phone: '', secondary_phone: '', email: '', is_emergency: '', state: '' });
const emptyMed = () => ({ medication: '', dosage: '', frequency: '' });

export default function Prescreen({ user }) {
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [existingPrescreen, setExistingPrescreen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [autoStatus, setAutoStatus] = useState(null);
  const [autoErr, setAutoErr] = useState('');
  const dirtyRef = useRef(false);
  const autosaveBusyRef = useRef(false);
  const statusRef = useRef('draft');
  const existingRef = useRef(null);
  const [openSections, setOpenSections] = useState({
    basic: true, facesheet: false, treatment: false, substances: false,
    mentalhealth: false, medical: false, functional: false, legal: false,
    employment: false, contacts: false, mhtrack: false, recommendation: true
  });
  const [form, setForm] = useState({ ...BLANK_FORM });
  const token = () => localStorage.getItem('tcio_token');
  const [viewMode, setViewMode] = useState('working');
  const [leadSearch, setLeadSearch] = useState('');
  const [doneList, setDoneList] = useState([]);
  const [viewPre, setViewPre] = useState(null);
  const [adminEditLeadId, setAdminEditLeadId] = useState(null);
  const isAdmin = ['admin', 'super_admin', 'ceo'].includes((user?.role || '').toLowerCase());
  // Prescreen edits are an admissions-owned function (Jaclyn ticket 07244657, Chad ruling 2026-09-04)
  const canEditPre = isAdmin || ['admissions'].includes((user?.role || '').toLowerCase());
  const handleAdminEdit = (pre) => {
    if (!pre?.lead_id) return;
    setAdminEditLeadId(pre.lead_id);
    setViewPre(null);
    setViewMode('working');
    setTimeout(() => setSelectedLeadId(pre.lead_id), 400);
  };
  const [emrLiveFacs, setEmrLiveFacs] = useState([]);
  const [facList, setFacList] = useState([]);
  const [facFilter, setFacFilter] = useState('');
  useEffect(() => {
    fetch(`${API}/api/facilities`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(j => { if (j.ok) { const d = j.data || []; setFacList(d); setEmrLiveFacs(d.filter(f => f.emr_live).map(f => String(f.id))); } })
      .catch(() => {});
  }, []);
  const facMatch = (fid) => !facFilter || String(fid || '') === facFilter;
  const kipuHiddenFor = (fid) => !!fid && emrLiveFacs.includes(String(fid));

  // ── PART A: WORK QUEUE DATA (spec A2-A10) ──
  const [dash, setDash] = useState(null);
  const [dashErr, setDashErr] = useState('');
  const [assignFilter, setAssignFilter] = useState('all'); // 'mine' | 'all' (leads.assigned_to; created_by is null on all rows - stamping docketed)
  const [homeSearch, setHomeSearch] = useState('');
  const [findOpen, setFindOpen] = useState(false);
  const [findSearch, setFindSearch] = useState('');
  const [showAllActive, setShowAllActive] = useState(false);
  const [kpiFilter, setKpiFilter] = useState(null); // 'need' | 'inprogress' | 'ready' | null - KPI cards double as table filters
  const loadDash = () => {
    fetch(`${API}/api/prescreens/dashboard`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(j => { if (j.ok) { setDash(j); setDashErr(''); } else setDashErr(j.error || 'Failed to load work queue'); })
      .catch(() => setDashErr('Network error loading work queue'));
  };
  useEffect(() => { loadDash(); }, []);
  useEffect(() => { if (!selectedLeadId) loadDash(); /* refresh queue on return from workspace */ // eslint-disable-next-line
  }, [selectedLeadId]);

  // One scoped pipeline: facility + assignment + search applied ONCE, everything downstream
  // (KPIs, priority, both tables, counts) derives from it - spec A3 atomic scoping.
  const scoped = useMemo(() => {
    const rows = (dash?.work || [])
      .filter(r => facMatch(r.facility_id))
      .filter(r => assignFilter !== 'mine' || String(r.assigned_to || '') === String(dash?.me?.id || ''))
      .filter(r => {
        if (!homeSearch.trim()) return true;
        const q = homeSearch.trim().toLowerCase();
        return `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) || String(r.phone || '').includes(q);
      })
      .map(r => ({ ...r, pri: priorityOf(r), ps: prescreenState(r) }));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const completedScoped = (dash?.completed || []).filter(r => facMatch(r.facility_id));
    return {
      rows,
      kpi: {
        need: rows.filter(r => !r.prescreen_id).length,
        inProgress: rows.filter(r => r.prescreen_id && r.prescreen_status === 'draft' && r.required_missing > 0).length,
        ready: rows.filter(r => r.ps.kind === 'review').length,
        completedToday: completedScoped.filter(r => new Date(r.updated_at) >= today).length,
      },
      priority: rows.filter(r => r.pri.level).sort((a, b) => b.pri.score - a.pri.score).slice(0, 3),
      attention: rows.filter(r => r.pri.score > 0 || r.ps.kind === 'review').sort((a, b) => b.pri.score - a.pri.score).slice(0, 8),
      completedCount: completedScoped.length,
    };
  }, [dash, facFilter, assignFilter, homeSearch]);

  useEffect(() => {
    if (viewMode !== 'completed') { setDoneList([]); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/prescreens`, { headers: { Authorization: `Bearer ${token()}` } });
        const j = await r.json();
        if (!j.ok || !Array.isArray(j.data)) return;
        const r2 = await fetch(`${API}/api/leads/v2?all=true`, { headers: { Authorization: `Bearer ${token()}` } });
        const j2 = await r2.json();
        const nameOf = {};
        const facOf = {};
        if (j2.ok) j2.data.forEach(l => { nameOf[l.id] = `${l.first_name || ''} ${l.last_name || ''}`.trim(); facOf[l.id] = l.facility_id; });
        setDoneList(j.data.map(p => ({ ...p, patient: nameOf[p.lead_id] || p.assessment_title || 'Unknown', lead_facility_id: facOf[p.lead_id] }))
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)));
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line
  }, [viewMode]);

  const fmtVal = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') { try { return JSON.stringify(v, null, 1).replace(/[{}\[\]"]/g, '').replace(/,\n/g, '\n').trim(); } catch { return String(v); } }
    return String(v);
  };
  const label = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const PRE_SECTIONS = [
    ['Overview', ['assessment_title','presenting_problem','source','status','dob','primary_phone','primary_email']],
    ['Demographics', ['mailing_street','city','mailing_state','mailing_zip','sex','marital_status','race','ethnicity','dietary']],
    ['Substance / Clinical', ['substances','treatment_history','mh_diagnosis','mh_symptoms','mh_current_struggles','suicidal_homicidal_ideation','past_si_hi','suicide_attempts','self_harm','other_addictive_behaviors','ever_overdosed','overdose_details','withdrawal_symptoms','withdrawal_details','mania_psychosis']],
    ['Medical', ['medical_conditions','allergies','current_medications','seizure_history','seizure_details','eating_disorder','eating_disorder_details','adl_skills','on_disability']],
    ['Psychosocial', ['contributing_factors','living_situation','how_support_use','aa_na','who_initiated','family_history','related_contacts']],
    ['Legal', ['sexual_crime_conviction','arson_conviction','pending_charges','charges_details','on_probation','probation_details','legal_history']],
    ['Employment', ['currently_employed','employer_knows','insurance_through_employer','needs_fmla']],
    ['Recommendation', ['recommend_dtx','recommend_rtc','recommend_php','recommend_iop','recommend_op','recommendation_to_admit','recommendation_notes','asam_summary']],
  ];
  const downloadPrePdf = (p) => {
    const doc = new jsPDF();
    let y = 16;
    const W = 182;
    doc.setFontSize(15); doc.setTextColor(26, 74, 107);
    doc.text(`Pre-Assessment ${DASH} ${p.patient}`, 14, y); y += 7;
    doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(`Status: ${p.status || DASH} ${DOT} Created ${new Date(p.created_at).toLocaleDateString()} ${DOT} Downloaded ${new Date().toLocaleString()}`, 14, y); y += 8;
    PRE_SECTIONS.forEach(([sec, keys]) => {
      const items = keys.map(k => [label(k), fmtVal(p[k])]).filter(x => x[1]);
      if (!items.length) return;
      if (y > 270) { doc.addPage(); y = 16; }
      doc.setFontSize(11); doc.setTextColor(26, 74, 107); doc.setFont(undefined, 'bold');
      doc.text(sec.toUpperCase(), 14, y); y += 5;
      doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
      items.forEach(([lb, val]) => {
        const lines = doc.splitTextToSize(`${lb}: ${val}`, W);
        if (y + lines.length * 4 > 285) { doc.addPage(); y = 16; }
        doc.text(lines, 14, y); y += lines.length * 4 + 1.5;
      });
      y += 3;
    });
    doc.save(`prescreen_${p.patient.replace(/\s+/g, '_')}_${String(p.created_at).slice(0,10)}.pdf`);
  };

  useEffect(() => {
    if (viewMode === 'working') {
      fetch(`${API}/api/leads/v2?all=true`, { headers: { Authorization: `Bearer ${token()}` } })
        .then(r => r.json())
        .then(d => {
          if (!d.ok) return;
          setLeads(d.data.filter(l =>
            l.pipeline_stage === 'pending_admit' || l.id === adminEditLeadId));
          const jump = localStorage.getItem('tcio_prescreen_jump');
          if (jump && d.data.some(l => l.id === jump)) {
            localStorage.removeItem('tcio_prescreen_jump');
            setTimeout(() => setSelectedLeadId(jump), 200);
          }
        });
    } else {
      fetch(`${API}/api/prescreens`, { headers: { Authorization: `Bearer ${token()}` } })
        .then(r => r.json())
        .then(async d => {
          if (!d.ok || !Array.isArray(d.data)) { setLeads([]); return; }
          const done = d.data.filter(p => p.lead_id);
          const ids = [...new Set(done.map(p => p.lead_id))];
          const r2 = await fetch(`${API}/api/leads/v2?all=true`, { headers: { Authorization: `Bearer ${token()}` } });
          const j2 = await r2.json();
          if (j2.ok) setLeads(j2.data.filter(l => ids.includes(l.id)));
        }).catch(() => setLeads([]));
    }
    setSelectedLeadId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, adminEditLeadId]);

  useEffect(() => {
    if (!selectedLeadId) { setSelectedLead(null); setExistingPrescreen(null); setForm({ ...BLANK_FORM }); return; }
    const lead = leads.find(l => l.id === selectedLeadId);
    setSelectedLead(lead);
    if (lead) {
      setForm({
        ...BLANK_FORM,
        assessment_title: `${lead.first_name} ${lead.last_name} ${DASH} Pre-Assessment`,
        primary_phone: lead.phone || '',
        primary_email: lead.email || '',
        dob: lead.dob ? String(lead.dob).slice(0,10) : '',
        source: lead.source_channel || '',
      });
    }
    fetch(`${API}/api/prescreens?lead_id=${selectedLeadId}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.data) {
          setExistingPrescreen(d.data);
          existingRef.current = d.data;
          statusRef.current = d.data.status || 'draft';
          setForm(d.data);
        } else {
          existingRef.current = null;
          statusRef.current = 'draft';
        }
        dirtyRef.current = false;
      })
      .catch(() => {});
  }, [selectedLeadId, leads]);

  const set = (k, v) => { dirtyRef.current = true; setForm(prev => ({ ...prev, [k]: v })); };

  // AUTOSAVE - 2s after the last edit, save silently. First save creates the
  // prescreen and remembers its id (no duplicates); after that it PATCHes.
  // status is preserved so editing a completed prescreen never downgrades it.
  useEffect(() => {
    if (!dirtyRef.current || !selectedLeadId) return;
    const t = setTimeout(async () => {
      if (autosaveBusyRef.current || saving) return;
      autosaveBusyRef.current = true;
      setAutoStatus('saving');
      try {
        const ex = existingRef.current;
        const method = ex ? 'PATCH' : 'POST';
        const url = ex ? `${API}/api/prescreens/${ex.id}` : `${API}/api/prescreens`;
        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ ...form, lead_id: selectedLeadId, status: statusRef.current }),
        });
        const d = await res.json();
        if (d.ok) {
          if (!ex && d.id) { existingRef.current = { id: d.id }; setExistingPrescreen({ id: d.id }); }
          dirtyRef.current = false;
          setAutoStatus('saved'); setAutoErr('');
        } else {
          setAutoStatus('error'); setAutoErr(d.error || ('HTTP ' + res.status));
        }
      } catch (e) {
        setAutoStatus('error'); setAutoErr(e.message || 'network error');
      }
      autosaveBusyRef.current = false;
    }, 2000);
    return () => clearTimeout(t);
  }, [form, selectedLeadId, saving]);

  // ── AI ASSIST ──
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCall, setAiCall] = useState({ busy: false, done: false });
  const [aiErr, setAiErr] = useState('');
  const [aiWrongPerson, setAiWrongPerson] = useState(false);
  const [dict, setDict] = useState('');
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const [txStatus, setTxStatus] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(null);
  const [rejectedFills, setRejectedFills] = useState({});
  useEffect(() => {
    if (!selectedLeadId) { setTxStatus(null); return; }
    fetch(`${API}/api/prescreens/transcript-status?lead_id=${selectedLeadId}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(j => { if (j.ok) setTxStatus(j); }).catch(() => {});
  }, [selectedLeadId]);
  // CALL WITH AI (Chad 10/5): Faith, the screening agent, calls the client through CallRail. The call is tied to this lead.
  const callWithAi = async () => {
    if (selectedLeadId === null || selectedLeadId === undefined || selectedLeadId === '') { setAiErr('Open this prescreen from the client\u2019s lead page first, so the call is tied to the right person.'); return; }
    const agreed = window.confirm('Faith, the automated assistant, will call this client now and go through the screening.\n\nClick OK only if the client agreed to an automated screening call.');
    if (agreed === false) return;
    setAiCall({ busy: true, done: false }); setAiErr('');
    try {
      const r = await fetch(`${API}/api/call/dial-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ lead_id: selectedLeadId, client_agreed: true }),
      });
      const j = await r.json();
      if (j.ok) { setAiCall({ busy: false, done: true }); setTimeout(() => setAiCall({ busy: false, done: false }), 90000); }
      else { setAiCall({ busy: false, done: false }); setAiErr(j.error || 'The call could not be placed.'); }
    } catch (e) { setAiCall({ busy: false, done: false }); setAiErr('Connection error'); }
  };
  const runAssist = async (dictation, confirmIdentity) => {
    if (!selectedLeadId) { setAiErr('This prescreen is not linked to a lead. Open the prescreen from the client\u2019s lead page so the AI can read their call transcripts.'); return; }
    setAiBusy(true); setAiErr('');
    try {
      const r = await fetch(`${API}/api/prescreens/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ lead_id: selectedLeadId, current_form: form, dictation: dictation || undefined, confirm_identity: confirmIdentity === true ? true : undefined }),
      });
      const j = await r.json();
      if (!j.ok) { setAiErr(j.error || 'AI assist failed'); setAiWrongPerson(false); }
      else if (j.empty) { setAiErr(j.message); setAiWrongPerson(!!j.wrong_person); }
      else { setAiWrongPerson(false); setAi(j); setReviewIdx(null); setRejectedFills({}); if (dictation) setDict(''); autoApplyResult(j); }
    } catch (e) { setAiErr('Connection error'); }
    setAiBusy(false);
  };
  const stillEmptyCheck = (cur) => cur === '' || cur === null || cur === undefined || (Array.isArray(cur) && cur.length === 0) || cur === false;
  const BANNED_FILLS = ['asam_summary','ai_gaps','id','lead_id','status'];
  const applyOneFill = (k) => {
    if (!ai?.fills || !(k in ai.fills) || BANNED_FILLS.includes(k)) return;
    setForm(prev => stillEmptyCheck(prev[k]) ? { ...prev, [k]: ai.fills[k] } : prev);
    dirtyRef.current = true;
  };
  const rejectOneFill = (k) => setRejectedFills(prev => ({ ...prev, [k]: true }));
  // AI-DOES-THE-WORK (2026-09-10, Chad ruling): generation writes straight into
  // the form - empty fields only, human work never overwritten - human reads and
  // edits after. The old review-then-apply click flow is gone.
  // A table row with nothing chosen in its main box is an empty row: the fill-in may write over it.
  const ROW_MAIN = { substances: 'substance', current_medications: 'medication', treatment_history: 'facility_name', related_contacts: 'name' };
  const rowsBlank = (k, cur) => Boolean(ROW_MAIN[k]) && Array.isArray(cur) && cur.every(r => (r && r[ROW_MAIN[k]]) ? false : true);
  const autoApplyResult = (res) => {
    if (!res || res.empty) return;
    setForm(prev => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(res.fills || {})) {
        if (BANNED_FILLS.includes(k)) continue;
        if (stillEmptyCheck(prev[k]) || rowsBlank(k, prev[k])) next[k] = v;
      }
      return next;
    });
    if (res.asam?.medical_necessity_summary) {
      const asamText = [`${DASH} ASAM ASSESSMENT ${DASH}`,
        ...['d1','d2','d3','d4','d5','d6'].map((d,i) =>
          `Dimension ${i+1}${res.asam[d]?.severity != null ? ' (severity ' + res.asam[d].severity + ')' : ''}: ${res.asam[d]?.narrative || 'No evidence.'}`),
        '', 'RECOMMENDED LOC: ' + (res.asam.loc_recommendation || DASH),
        '', 'MEDICAL NECESSITY: ' + res.asam.medical_necessity_summary
      ].join('\n');
      const asamJson = JSON.stringify(res.asam);
      // ASAM renders as its own document section below - notes stay human-only.
      setForm(prev => ({ ...prev, asam_summary: asamJson }));
    }
    dirtyRef.current = true;
    setAi(prev => (prev ? { ...prev, applied: true } : prev));
  };
  const applyFills = () => { if (ai) autoApplyResult(ai); };
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setAiErr('Dictation needs Chrome or Safari.'); return; }
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = 'en-US';
    let committed = '';
    rec.onstart = () => { setDict(prev => { committed = prev; return prev; }); };
    rec.onresult = (e) => {
      let finals = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) finals += e.results[i][0].transcript + ' ';
        else interim += e.results[i][0].transcript + ' ';
      }
      setDict((committed + ' ' + finals + interim).trim());
    };
    rec.onend = () => setListening(false);
    recogRef.current = rec; rec.start(); setListening(true);
  };
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  const saveAndNext = (id) => {
    const order = SECTION_DEFS.map(d => d.id);
    const idx = order.indexOf(id);
    let nextId = null;
    for (let i = 1; i < order.length; i++) {
      const cand = SECTION_DEFS[(idx + i) % order.length];
      const st = sectionStatus(cand, form);
      if (!st.complete) { nextId = cand.id; break; }
    }
    setOpenSections(prev => {
      const next = { ...prev, [id]: false };
      if (nextId) next[nextId] = true;
      return next;
    });
  };
  const addSubstance = () => set('substances', [...form.substances, emptySubstance()]);
  const updateSubstance = (i, k, v) => { const arr = [...form.substances]; arr[i] = { ...arr[i], [k]: v }; set('substances', arr); };
  const removeSubstance = (i) => set('substances', form.substances.filter((_, idx) => idx !== i));
  const addTreatment = () => set('treatment_history', [...form.treatment_history, emptyTreatment()]);
  const updateTreatment = (i, k, v) => { const arr = [...form.treatment_history]; arr[i] = { ...arr[i], [k]: v }; set('treatment_history', arr); };
  const removeTreatment = (i) => set('treatment_history', form.treatment_history.filter((_, idx) => idx !== i));
  const addContact = () => set('related_contacts', [...form.related_contacts, emptyContact()]);
  const updateContact = (i, k, v) => { const arr = [...form.related_contacts]; arr[i] = { ...arr[i], [k]: v }; set('related_contacts', arr); };
  const removeContact = (i) => set('related_contacts', form.related_contacts.filter((_, idx) => idx !== i));
  const addMed = () => set('current_medications', [...form.current_medications, emptyMed()]);
  const updateMed = (i, k, v) => { const arr = [...form.current_medications]; arr[i] = { ...arr[i], [k]: v }; set('current_medications', arr); };
  const removeMed = (i) => set('current_medications', form.current_medications.filter((_, idx) => idx !== i));

  const handleSave = async (status = 'complete') => {
    if (!selectedLeadId) { setSaveMsg({ type: 'error', text: 'Please select a lead first.' }); return null; }
    setSaving(true);
    setSaveMsg(null);
    let savedId = null;
    try {
      const method = existingPrescreen ? 'PATCH' : 'POST';
      const url = existingPrescreen ? `${API}/api/prescreens/${existingPrescreen.id}` : `${API}/api/prescreens`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ...form, lead_id: selectedLeadId, status }),
      });
      const d = await res.json();
      if (d.ok) {
        setSaveMsg({ type: 'success', text: status === 'draft' ? CHECK + ' Saved as draft' : CHECK + ' Pre-assessment complete' });
        savedId = (existingPrescreen && existingPrescreen.id) || d.id || null;
        if (!existingPrescreen && d.id) { setExistingPrescreen({ id: d.id }); existingRef.current = { id: d.id }; }
        if (existingPrescreen && !existingRef.current) existingRef.current = existingPrescreen;
        statusRef.current = status;
        dirtyRef.current = false;
        setTimeout(() => setSaveMsg(null), 4000);
      } else {
        setSaveMsg({ type: 'error', text: d.error || 'Save failed' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: 'Network error' });
    }
    setSaving(false);
    return savedId;
  };
  const handleSendEmail = async () => {
    if (!selectedLeadId) { setSaveMsg({ type: 'error', text: 'Please select a lead first.' }); return; }
    setSending(true);
    // CRITICAL: the email is built from the DB row. Save on-screen answers first;
    // only send if the save succeeded (fixes the "clicked No but email says Yes" bug).
    const savedId = await handleSave('complete');
    if (!savedId) { setSending(false); return; }
    setSaveMsg(null);
    try {
      const res = await fetch(`${API}/api/prescreens/${savedId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          lead_name: selectedLead ? `${selectedLead.first_name} ${selectedLead.last_name}` : 'Unknown',
          facility_id: selectedLead?.facility_id || '',
          facility_name: selectedLead?.facility_name || '[FACILITY NAME UNAVAILABLE]',
          submitted_by: user?.name || 'Staff',
        }),
      });
      const d = await res.json();
      if (d.ok) {
        setSaveMsg({ type: 'success', text: `${CHECK} Sent to ${d.sent_to.join(', ')}` });
      } else {
        setSaveMsg({ type: 'error', text: d.error || 'Send failed' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: 'Network error' });
    }
    setSending(false);
  };
  const [kipuPush, setKipuPush] = useState({});
  const handleKipuPush = async (preId) => {
    if (!preId || kipuPush[preId] === 'sending') return;
    setKipuPush(k => ({ ...k, [preId]: 'sending' }));
    try {
      const r = await fetch(`${API}/api/kipu/prescreens/${preId}/push`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.ok) {
        setKipuPush(k => ({ ...k, [preId]: 'done' }));
        setSaveMsg({ type: 'success', text: CHECK + ' Sent to Kipu ' + DASH + ' accept it in the Kipu queue' });
      } else {
        setKipuPush(k => ({ ...k, [preId]: undefined }));
        setSaveMsg({ type: 'error', text: d.error || 'Kipu push failed' });
      }
    } catch (e) {
      setKipuPush(k => ({ ...k, [preId]: undefined }));
      setSaveMsg({ type: 'error', text: 'Network error pushing to Kipu' });
    }
  };
  const handlePrint = async () => {
    if (!existingPrescreen) return;
    try {
      const r = await fetch(`${API}/api/prescreens/${existingPrescreen.id}/pdf`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) { setSaveMsg({ type: 'error', text: 'PDF generation failed ' + DASH + ' save the prescreen first, then retry.' }); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="(.+)"/);
      a.download = m ? m[1] : 'Pre-Assessment.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setSaveMsg({ type: 'error', text: 'Download failed' }); }
  };

  // ── SHARED ROW ACTION ──
  const openLead = (leadId) => { setFindOpen(false); setViewMode('working'); setAdminEditLeadId(null); setSelectedLeadId(leadId); };
  const rowAction = (r) => r.ps.kind === 'review' ? 'Review' : r.prescreen_id ? 'Continue' : 'Start';

  const WorkTable = ({ rows, title }) => (
    <div style={{ ...s.card, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${tk.borderSoft}`, fontSize: 12.5, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={s.th}>Patient</th><th style={s.th}>Facility</th><th style={s.th}>Status</th>
            <th style={s.th}>Prescreen</th><th style={s.th}>Last Activity</th><th style={{ ...s.th, textAlign: 'right' }}>Action</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.lead_id} style={{ cursor: 'pointer' }} onClick={() => openLead(r.lead_id)}>
                <td style={s.td}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Avatar first={r.first_name} last={r.last_name} />
                    <span>
                      <div style={{ fontWeight: 700 }}>{r.first_name} {r.last_name}</div>
                      <div style={{ fontSize: 11.5, color: tk.textMuted }}>{r.phone || 'No phone'}</div>
                    </span>
                  </span>
                </td>
                <td style={s.td}><span style={{ fontSize: 12.5, color: tk.textSecondary }}>{r.facility_name || DASH}</span></td>
                <td style={s.td}>
                  <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {r.crisis_flag && <span style={s.chip(tk.dangerBg, tk.danger, tk.danger)}><Flame {...ICS} color={tk.danger} />CRISIS</span>}
                    <span style={s.chip(tk.warningBg, tk.warning)}>{(r.pipeline_stage || '').replace(/_/g, ' ').toUpperCase()}</span>
                    {r.vob_returned && <span style={s.chip(tk.successBg, tk.success)}>VOB {CHECK}</span>}
                  </span>
                </td>
                <td style={s.td}>
                  {r.ps.kind === 'progress' && r.ps.pct != null ? <MiniBar pct={r.ps.pct} />
                    : r.ps.kind === 'review' ? <span style={s.chip(tk.purpleSoft, tk.purple, tk.purple)}>Ready for Review</span>
                    : <span style={{ fontSize: 12.5, color: tk.textMuted }}>Not Started</span>}
                </td>
                <td style={s.td}>
                  <span style={{ fontSize: 12, color: tk.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <PhoneCall {...ICS} color={tk.teal} />
                    {r.last_call_at ? `Call ${timeAgo(r.last_call_at)}` : 'No calls'}
                    {(r.calls_complete || 0) > 0 && <span style={{ color: tk.textMuted }}>{DOT} {r.calls_complete} analyzed</span>}
                  </span>
                </td>
                <td style={{ ...s.td, textAlign: 'right' }}>
                  <button onClick={(e) => { e.stopPropagation(); openLead(r.lead_id); }}
                    style={{ ...s.btn(rowAction(r) === 'Review' ? tk.purpleSoft : tk.aiSoft, rowAction(r) === 'Review' ? tk.purple : tk.ai, rowAction(r) === 'Review' ? tk.purple : tk.ai), padding: '6px 14px', fontSize: 12.5 }}>
                    {rowAction(r)}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={{ ...s.td, color: tk.textMuted, textAlign: 'center', padding: '18px' }} colSpan={6}>No records in this scope.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ═══════════ PART A: PRESCREEN HOME / WORK QUEUE ═══════════
  if (!selectedLeadId) {
    const caughtUp = dash && scoped.kpi.need === 0 && scoped.attention.length === 0 && viewMode === 'working';
    const kpiRows = kpiFilter === 'need' ? scoped.rows.filter(r => !r.prescreen_id)
      : kpiFilter === 'inprogress' ? scoped.rows.filter(r => r.prescreen_id && r.prescreen_status === 'draft' && r.required_missing > 0)
      : kpiFilter === 'ready' ? scoped.rows.filter(r => r.ps.kind === 'review')
      : scoped.rows;
    const kpiTitle = kpiFilter === 'need' ? 'Not Started ' + DASH + ' Need Prescreen' : kpiFilter === 'inprogress' ? 'In Progress' : kpiFilter === 'ready' ? 'Ready for Review' : 'All Active Prescreens';
    return (
      <div style={s.page}>
        <div style={s.pageTitle}>Pre-Assessment <ClipboardCheck {...IC} color={tk.blue600} /></div>
        <div style={s.pageSub}>Complete clinical pre-assessments prior to admission</div>

        {/* A2 GLOBAL CONTROL BAR */}
        <div style={{ ...s.card, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase' }}>Facility</span>
            <select value={facFilter} onChange={e => setFacFilter(e.target.value)} style={s.select}>
              <option value="">All Facilities</option>
              {facList.map(f => <option key={f.id} value={String(f.id)}>{f.org_name}</option>)}
            </select>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase' }}>Assignment</span>
            <select value={assignFilter} onChange={e => setAssignFilter(e.target.value)} style={s.select}>
              <option value="all">All Prescreeners</option>
              <option value="mine">My Prescreens</option>
            </select>
          </span>
          <span style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
            <Search {...ICS} color={tk.textMuted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input placeholder="Search patient or phone..." value={homeSearch} onChange={e => setHomeSearch(e.target.value)}
              style={{ ...s.input, width: '100%', boxSizing: 'border-box', paddingLeft: 34 }} />
          </span>
          <button onClick={() => { setFindSearch(''); setFindOpen(true); }} style={s.btn(tk.ai, '#fff')}><Plus {...ICS} color="#fff" />New / Find Patient</button>
        </div>

        {dashErr && <div style={{ ...s.card, padding: '12px 16px', marginBottom: 14, color: tk.danger, fontSize: 13, fontWeight: 600 }}>{dashErr}</div>}

        {/* A4 KPI STRIP */}
        {dash && viewMode === 'working' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['need', scoped.kpi.need, 'Need Prescreen', Users, tk.blue700, 'New calls ready'],
              ['inprogress', scoped.kpi.inProgress, 'In Progress', Timer, tk.teal, 'Being completed'],
              ['ready', scoped.kpi.ready, 'Ready for Review', FileCheck2, tk.purple, 'Awaiting review'],
              ['done', scoped.kpi.completedToday, 'Completed Today', CalendarCheck, tk.success, 'Pre-assessments'],
            ].map(([id, n, lbl, Icon, color, sub]) => (
              <div key={lbl} onClick={() => id === 'done' ? setViewMode('completed') : setKpiFilter(f => f === id ? null : id)}
                title={id === 'done' ? 'View completed pre-assessments' : kpiFilter === id ? 'Clear filter' : 'Filter the list to these'}
                style={{ ...s.card, padding: '12px 15px', display: 'flex', gap: 11, alignItems: 'center', cursor: 'pointer', border: kpiFilter === id ? `1.5px solid ${color}` : `1px solid ${tk.borderSoft}` }}>
                <Icon size={20} strokeWidth={1.75} color={color} />
                <span>
                  <div style={{ fontSize: 21, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{n}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tk.textSecondary }}>{lbl}</div>
                  <div style={{ fontSize: 10.5, color: tk.textMuted }}>{sub}</div>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* A5 TCIO PRIORITY - explainable signals only */}
        {dash && viewMode === 'working' && scoped.priority.length > 0 && (
          <div style={{ ...s.card, border: `1.5px solid ${tk.purple}`, padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, color: tk.purple, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 9 }}>
              <Sparkles {...ICS} color={tk.purple} />TCIO Priority
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
              {scoped.priority.map(r => (
                <div key={r.lead_id} onClick={() => openLead(r.lead_id)}
                  style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', padding: '8px 10px', borderRadius: tk.radiusControl, background: tk.surfaceSoft }}>
                  <Avatar first={r.first_name} last={r.last_name} />
                  <span style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 13.5, color: tk.ink }}>{r.first_name} {r.last_name}</span>
                      <span style={s.chip(r.pri.level === 'HIGH' ? tk.dangerBg : tk.warningBg, r.pri.level === 'HIGH' ? tk.danger : tk.warning)}>{r.pri.level}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: tk.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.pri.reasons.join(' ' + DOT + ' ')}</div>
                  </span>
                  <ChevronRight {...ICS} color={tk.textMuted} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* A6 WORKING / COMPLETED NAVIGATION */}
        <div style={{ display: 'flex', gap: 2, borderBottom: `2px solid ${tk.borderSoft}`, marginBottom: 14 }}>
          {[['working', 'Working', dash ? scoped.rows.length : null], ['completed', 'Completed', dash ? scoped.completedCount : null]].map(([mode, lbl, n]) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{ background: 'none', border: 'none', borderBottom: viewMode === mode ? `2.5px solid ${tk.ai}` : '2.5px solid transparent', marginBottom: -2, padding: '8px 16px', fontSize: 13.5, fontWeight: 700, color: viewMode === mode ? tk.ai : tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit' }}>
              {lbl}{n != null ? ` ${n}` : ''}
            </button>
          ))}
        </div>

        {viewMode === 'working' ? (
          !dash ? <div style={{ ...s.card, padding: '30px', textAlign: 'center', color: tk.textMuted, fontSize: 13 }}>Loading work queue{ELLIP}</div>
          : caughtUp ? (
            <div style={{ ...s.card, padding: '44px 20px', textAlign: 'center' }}>
              <CheckCircle2 size={32} strokeWidth={1.5} color={tk.success} style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink }}>You{'\u2019'}re caught up</div>
              <div style={{ fontSize: 13, color: tk.textSecondary, marginTop: 4, marginBottom: 14 }}>No prescreens currently require attention.</div>
              <button onClick={() => setViewMode('completed')} style={s.btn(tk.surface, tk.ai, tk.ai)}>View Completed Prescreens</button>
            </div>
          ) : (
            <>
              {!kpiFilter && scoped.attention.length > 0 && <WorkTable rows={scoped.attention} title="Needs Your Attention" />}
              <WorkTable rows={showAllActive || kpiFilter ? kpiRows : kpiRows.slice(0, 12)} title={kpiTitle} />
              {!kpiFilter && scoped.rows.length > 12 && (
                <div style={{ textAlign: 'center', marginBottom: 14 }}>
                  <button onClick={() => setShowAllActive(v => !v)} style={{ background: 'none', border: 'none', color: tk.ai, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {showAllActive ? 'Show Fewer' : `View All Active Prescreens (${scoped.rows.length})`} {ARROW}
                  </button>
                </div>
              )}
            </>
          )
        ) : (
          /* A14 COMPLETED dense table */
          <div style={{ ...s.card, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={s.th}>Patient</th><th style={s.th}>Status</th><th style={s.th}>LOC</th>
                  <th style={s.th}>Admit Rec</th><th style={s.th}>Updated</th><th style={{ ...s.th, textAlign: 'right' }}>Actions</th>
                </tr></thead>
                <tbody>
                  {doneList.filter(p => facMatch(p.lead_facility_id)).filter(p => !homeSearch.trim() || p.patient.toLowerCase().includes(homeSearch.trim().toLowerCase())).map(p => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setViewPre(p)}>
                      <td style={s.td}><span style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar first={(p.patient || '?').split(' ')[0]} last={(p.patient || '').split(' ')[1] || ''} /><b>{p.patient}</b></span></td>
                      <td style={s.td}><span style={s.chip(tk.surfaceSoft, tk.textSecondary, tk.borderSoft)}>{(p.status || 'draft').replace(/_/g, ' ')}</span></td>
                      <td style={s.td}><span style={{ fontSize: 12.5 }}>{LOC_OPTIONS.filter((_, i) => p[['recommend_dtx','recommend_rtc','recommend_php','recommend_iop','recommend_op'][i]]).join(', ') || DASH}</span></td>
                      <td style={s.td}>{p.recommendation_to_admit === true ? <span style={s.chip(tk.successBg, tk.success)}>Admit</span> : p.recommendation_to_admit === false ? <span style={s.chip(tk.dangerBg, tk.danger)}>No Admit</span> : <span style={{ color: tk.textMuted }}>{DASH}</span>}</td>
                      <td style={s.td}><span style={{ fontSize: 12, color: tk.textSecondary }}>{new Date(p.updated_at || p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => setViewPre(p)} style={{ ...s.btn(tk.aiSoft, tk.ai, tk.ai), padding: '5px 12px', fontSize: 12 }}>View</button>
                          <button onClick={() => downloadPrePdf(p)} style={{ ...s.btn(tk.surface, tk.teal, tk.teal), padding: '5px 12px', fontSize: 12 }}>PDF</button>
                        </span>
                      </td>
                    </tr>
                  ))}
                  {doneList.length === 0 && <tr><td style={{ ...s.td, color: tk.textMuted, textAlign: 'center', padding: '18px' }} colSpan={6}>No completed pre-assessments yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* A11 FIND PATIENT MODAL - duplicate guard: existing prescreen always Continues */}
        {findOpen && (
          <div onClick={() => setFindOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: tk.surface, borderRadius: tk.radiusCard, width: 'min(520px, 95vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(10,34,57,0.35)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 18px', borderBottom: `1px solid ${tk.borderSoft}` }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: tk.ink }}>Start Pre-Assessment</span>
                <button onClick={() => setFindOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X {...ICS} color={tk.textMuted} /></button>
              </div>
              <div style={{ padding: '12px 18px' }}>
                <div style={{ position: 'relative' }}>
                  <Search {...ICS} color={tk.textMuted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
                  <input autoFocus placeholder="Search patient or phone..." value={findSearch} onChange={e => setFindSearch(e.target.value)}
                    style={{ ...s.input, width: '100%', boxSizing: 'border-box', paddingLeft: 34 }} />
                </div>
              </div>
              <div style={{ overflowY: 'auto', padding: '0 18px 16px' }}>
                {(dash?.work || []).filter(r => facMatch(r.facility_id))
                  .filter(r => { const q = findSearch.trim().toLowerCase(); return !q || `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) || String(r.phone || '').includes(q); })
                  .slice(0, 10).map(r => (
                    <div key={r.lead_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${tk.borderSoft}` }}>
                      <span style={{ display: 'flex', gap: 9, alignItems: 'center', minWidth: 0 }}>
                        <Avatar first={r.first_name} last={r.last_name} />
                        <span style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: tk.ink }}>{r.first_name} {r.last_name}</div>
                          <div style={{ fontSize: 11.5, color: tk.textMuted }}>{r.facility_name || DASH} {DOT} {r.prescreen_id ? (r.ps?.label || prescreenState(r).label) + ' prescreen' : 'No prescreen'}</div>
                        </span>
                      </span>
                      <button onClick={() => openLead(r.lead_id)} style={{ ...s.btn(r.prescreen_id ? tk.aiSoft : tk.ai, r.prescreen_id ? tk.ai : '#fff', r.prescreen_id ? tk.ai : undefined), padding: '6px 13px', fontSize: 12, flexShrink: 0 }}>
                        {r.prescreen_id ? 'Continue' : 'Start New'}
                      </button>
                    </div>
                  ))}
                <div style={{ fontSize: 11.5, color: tk.textMuted, paddingTop: 10 }}>Showing pending-admit leads in your scope. Move a lead to Pending Admit from the Leads page to start their prescreen.</div>
              </div>
            </div>
          </div>
        )}

        {/* Completed view modal (shared) */}
        {viewPre && (
          <div onClick={() => setViewPre(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: tk.surface, borderRadius: tk.radiusCard, width: 'min(760px, 95vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(10,34,57,0.35)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: `1px solid ${tk.borderSoft}`, gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: tk.ink }}>Pre-Assessment {DASH} {viewPre.patient}</span>
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canEditPre && viewPre.lead_id && (
                    <button onClick={() => handleAdminEdit(viewPre)} style={s.btn(tk.warningBg, tk.warning, tk.warning)}><Pencil {...ICS} color={tk.warning} />Edit</button>
                  )}
                  <button onClick={() => downloadPrePdf(viewPre)} style={s.btn(tk.blue600, '#fff')}><Download {...ICS} color="#fff" />PDF</button>
                  <button onClick={() => handleKipuPush(viewPre.id)} disabled={kipuPush[viewPre.id] === 'sending'}
                    style={{ ...s.btn(kipuPush[viewPre.id] === 'done' ? tk.successBg : tk.purpleSoft, kipuPush[viewPre.id] === 'done' ? tk.success : tk.purple, kipuPush[viewPre.id] === 'done' ? tk.success : tk.purple), display: kipuHiddenFor(viewPre.lead_facility_id) ? 'none' : undefined }}>
                    {kipuPush[viewPre.id] === 'sending' ? 'Sending' + ELLIP : kipuPush[viewPre.id] === 'done' ? CHECK + ' Kipu' : ARROW + ' Kipu'}</button>
                  <button onClick={() => setViewPre(null)} style={s.btn(tk.surfaceSoft, tk.textSecondary, tk.borderSoft)}><X {...ICS} color={tk.textSecondary} /></button>
                </span>
              </div>
              <div style={{ overflowY: 'auto', padding: '14px 20px 22px' }}>
                {PRE_SECTIONS.map(([sec, keys]) => {
                  const items = keys.map(k => [label(k), fmtVal(viewPre[k])]).filter(x => x[1]);
                  if (!items.length) return null;
                  return (
                    <div key={sec} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: tk.blue700, textTransform: 'uppercase', borderBottom: `1px solid ${tk.borderSoft}`, paddingBottom: 3, marginBottom: 6, letterSpacing: '0.05em' }}>{sec}</div>
                      {items.map(([lb, val]) => (
                        <div key={lb} style={{ fontSize: 13, marginBottom: 4, lineHeight: 1.5, color: tk.ink }}>
                          <b style={{ color: tk.textSecondary }}>{lb}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{val}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════ PART B: CLIENT WORKSPACE (carried from 66cfe6c; rebuilt in Steps 2-8) ═══════════
  const missing = missingRequired(form);
  const reqDone = REQUIRED_FIELDS.length - missing.length;
  const pct = Math.round(100 * reqDone / REQUIRED_FIELDS.length);
  const allComplete = missing.length === 0;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
  const age = form.dob ? Math.floor((Date.now() - new Date(form.dob)) / 31557600000) : null;
  const pendingFills = ai && !ai.empty ? Object.keys(ai.fills || {}).filter(k => !BANNED_FILLS.includes(k) && !rejectedFills[k] && stillEmptyCheck(form[k])) : [];

  const AutoPill = () => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
      background: autoStatus === 'error' ? tk.dangerBg : autoStatus === 'saving' ? tk.warningBg : tk.successBg,
      color: autoStatus === 'error' ? tk.danger : autoStatus === 'saving' ? tk.warning : tk.success,
      visibility: autoStatus ? 'visible' : 'hidden' }}>
      {autoStatus === 'saving' ? 'Autosaving' + ELLIP : autoStatus === 'saved' ? 'Autosaved ' + CHECK : 'Autosave failed ' + DASH + ' retrying as you type'}
    </span>
  );
  const ProgressBar = ({ width }) => (
    <div style={{ width: width || 160 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: allComplete ? tk.success : tk.ai, fontVariantNumeric: 'tabular-nums' }}>{pct}% <span style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted }}>Complete</span></span>
        <span style={{ fontSize: 11, color: tk.textMuted, fontVariantNumeric: 'tabular-nums' }}>{missing.length} required left</span>
      </div>
      <div style={{ height: 6, background: tk.surfaceSoft, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: allComplete ? tk.success : tk.ai, borderRadius: 3, transition: 'width 250ms' }} />
      </div>
    </div>
  );

  return (
    <div style={{ ...s.page, paddingBottom: 120 }}>
      {/* B1 PATIENT HEADER */}
      <div style={{ ...s.card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 90, marginBottom: 12 }}>
        <div style={{ minWidth: 0, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={() => setSelectedLeadId('')} title="Back to the Prescreen work queue (autosave has your work)"
            style={{ ...s.btn(tk.surfaceSoft, tk.textSecondary, tk.borderSoft), padding: '7px 12px', flexShrink: 0 }}>{'\u2190'} Queue</button>
          <Avatar first={selectedLead?.first_name} last={selectedLead?.last_name} />
          <span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: tk.ink }}>{selectedLead ? `${selectedLead.first_name} ${selectedLead.last_name}` : 'Patient'}</span>
              <span style={s.chip(tk.warningBg, tk.warning)}>{(selectedLead?.pipeline_stage || '').replace(/_/g, ' ').toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {age != null && !isNaN(age) && <span>{age} yrs</span>}
              {selectedLead?.phone && <span>{selectedLead.phone}</span>}
              {txStatus && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><PhoneCall {...ICS} color={tk.teal} />{(txStatus.transcribing || 0) > 0 ? `${txStatus.transcribing} transcribing${ELLIP}` : 'calls ready'}</span>}
              <span style={{ color: existingPrescreen ? tk.blue700 : tk.purple, fontWeight: 600 }}>{existingPrescreen ? 'Editing existing pre-assessment' : 'New pre-assessment'}</span>
            </div>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <AutoPill />
          <ProgressBar />
          <button onClick={() => { if (window.confirm('Return to the work queue? Autosave has your work.')) setSelectedLeadId(''); }} style={s.btn(tk.surface, tk.textSecondary, tk.border)}>Change Patient</button>
        </div>
      </div>

      <div style={{ display: isMobile ? 'block' : 'grid', gridTemplateColumns: isMobile ? undefined : '1fr 300px', gap: 16, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>

        {/* B3/B4 TCIO PRESCREEN ASSISTANT */}
        <div style={{ background: tk.surface, border: `1.5px solid ${tk.purple}`, borderRadius: tk.radiusCard, padding: '15px 18px', marginBottom: 12, boxShadow: tk.shadow }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 14, color: tk.purple, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <Sparkles {...IC} color={tk.purple} />TCIO Prescreen Assistant
            </span>
            <button onClick={callWithAi} disabled={aiCall.busy || aiCall.done} title="Faith, the automated assistant, calls the client and goes through the screening" style={{ ...s.btn(tk.aiSoft, tk.ai, tk.ai), marginLeft: 'auto', marginRight: 8, opacity: (aiCall.busy || aiCall.done) ? 0.7 : 1 }}>{aiCall.busy ? 'Calling' + ELLIP : (aiCall.done ? 'Faith is calling the client' : 'Call with AI')}</button>
            <button onClick={() => runAssist()} disabled={aiBusy} style={{ ...s.btn(tk.purple, '#fff'), opacity: aiBusy ? 0.7 : 1 }}>
              {aiBusy ? 'Reading calls' + ELLIP : 'Generate from calls'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 4 }}>
            Reads completed call transcripts and writes the prescreen for you {DASH} fills every empty field (never overwrites what you typed), writes the ASAM six-dimension analysis with a level-of-care recommendation, and lists callback questions. Read it over and correct anything that needs it.
          </div>
          {txStatus && txStatus.transcribing > 0 && (
            <div style={{ marginTop: 8, color: tk.warning, background: tk.warningBg, border: `1px solid ${tk.warning}`, borderRadius: tk.radiusControl, padding: '6px 10px', fontSize: 12.5, fontWeight: 600 }}>
              {txStatus.transcribing} call{txStatus.transcribing === 1 ? '' : 's'} still transcribing {DASH} long calls can take 15-20 minutes after hangup; generate again once this clears.
            </div>
          )}
          {aiErr && <div style={{ marginTop: 8, color: tk.danger, fontSize: 13 }}>{aiErr}</div>}
          {aiErr && aiWrongPerson && (
            <button disabled={aiBusy} onClick={() => { if (window.confirm('Only confirm this if you listened to the call and it was this client. The date of birth heard on the call will be recorded as a mis-hearing, under your name.')) runAssist(undefined, true); }} style={{ ...s.btn(tk.warningBg, tk.warning, tk.warning), marginTop: 8 }}>That was them {DASH} use this call</button>
          )}
          {ai && !ai.empty && (
            <>
              <div style={{ display: 'flex', gap: 0, marginTop: 12, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, overflow: 'hidden', flexWrap: 'wrap' }}>
                {[[ai.transcripts_used || 0, 'Calls Analyzed', tk.blue700], [Object.keys(ai.fills || {}).length, 'Fields Proposed', tk.purple], [pendingFills.length, 'Need Confirmation', tk.warning], [(ai.gaps || []).length, 'Information Missing', tk.danger]].map(([n, lbl, color]) => (
                  <div key={lbl} style={{ flex: '1 1 110px', padding: '9px 12px', borderRight: `1px solid ${tk.borderSoft}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 19, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{n}</div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lbl}</div>
                  </div>
                ))}
              </div>
              {pendingFills.length > 0 && !ai.applied && (
                <div style={{ marginTop: 10, background: tk.purpleSoft, borderRadius: tk.radiusControl, padding: '11px 13px', border: `1px solid ${tk.purple}` }}>
                  {reviewIdx === null ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: tk.ink }}>{pendingFills.length} proposed fill{pendingFills.length === 1 ? '' : 's'} awaiting your review</span>
                      <span style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setReviewIdx(0)} style={s.btn(tk.surface, tk.purple, tk.purple)}>Review Suggestions {ARROW}</button>
                        <button onClick={applyFills} style={s.btn(tk.success, '#fff')}>{CHECK} Apply All</button>
                      </span>
                    </div>
                  ) : (() => {
                    const k = pendingFills[Math.min(reviewIdx, pendingFills.length - 1)];
                    if (!k) { setReviewIdx(null); return null; }
                    const v = ai.fills[k];
                    return (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: tk.purple, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Sparkles {...ICS} color={tk.purple} />AI Suggested {DOT} {Math.min(reviewIdx + 1, pendingFills.length)} of {pendingFills.length}</span>
                          <button onClick={() => setReviewIdx(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X {...ICS} color={tk.textMuted} /></button>
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: tk.ink, marginBottom: 2 }}>{label(k)}</div>
                        <div style={{ fontSize: 13, color: tk.ink, background: tk.surface, borderRadius: 6, padding: '7px 10px', marginBottom: 8, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>{typeof v === 'object' ? JSON.stringify(v, null, 1).replace(/[{}\[\]"]/g, '') : String(v)}</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => { applyOneFill(k); }} style={s.btn(tk.success, '#fff')}>{CHECK} Confirm</button>
                          <button onClick={() => { rejectOneFill(k); }} style={s.btn(tk.surface, tk.danger, tk.danger)}>Reject</button>
                          <span style={{ fontSize: 11.5, color: tk.textMuted, alignSelf: 'center' }}>Confirm applies to the form; edit it in its section any time.</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {ai.applied && <div style={{ marginTop: 8, color: tk.success, fontWeight: 700, fontSize: 12.5 }}>{CHECK} Applied {DASH} review the form, then Save</div>}
            </>
          )}
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <button onClick={toggleMic} title={listening ? 'Stop dictation' : 'Dictate new info'}
              style={{ ...s.btn(listening ? tk.danger : tk.surface, listening ? '#fff' : tk.purple, listening ? tk.danger : tk.purple), width: 40, height: 38, padding: 0, justifyContent: 'center' }}>
              {listening ? <Square {...ICS} color="#fff" /> : <Mic {...ICS} color={tk.purple} />}
            </button>
            <textarea value={dict} onChange={e => setDict(e.target.value)}
              placeholder={'Dictate or type new info ' + DASH + ' then Send. The AI updates only what changed.'}
              style={{ ...s.textarea, flex: 1, minHeight: 52 }} />
            <button onClick={() => runAssist(dict)} disabled={aiBusy || !dict.trim()}
              style={{ ...s.btn(tk.purple, '#fff'), opacity: dict.trim() && !aiBusy ? 1 : 0.5 }}>
              <Send {...ICS} color="#fff" />Send
            </button>
          </div>
        </div>

        {(() => {
          // Section shell hoisted to module-level PSection: defining it per-render gave it a new identity every keystroke, remounting all inputs and dropping focus (the 'cannot type' bug).
          const D = Object.fromEntries(SECTION_DEFS.map(d => [d.id, d]));
          return (
            <>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.basic}>
                <div style={s.fieldFull}><label style={s.label}>Assessment Title</label><input style={s.input} value={form.assessment_title} onChange={e => set('assessment_title', e.target.value)} /></div>
                <div style={s.grid2}><div style={s.field}><label style={s.label}>Source / Referral</label><input style={s.input} value={form.source} onChange={e => set('source', e.target.value)} /></div></div>
                <div style={s.fieldFull}><label style={s.label}>Presenting Problem</label><textarea style={s.textarea} value={form.presenting_problem} onChange={e => set('presenting_problem', e.target.value)} placeholder="Describe the presenting problem..." /></div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.facesheet}>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>Full Name</label><input style={{ ...s.input, background: tk.surfaceSoft, fontWeight: 700 }} value={selectedLead ? `${selectedLead.first_name} ${selectedLead.last_name}` : ''} readOnly /></div>
                  <div style={s.field}><label style={s.label}>Date of Birth</label><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="date" style={{ ...s.input, flex: 1 }} value={form.dob || ''} onChange={e => set('dob', e.target.value)} />{age != null && !isNaN(age) && <span style={{ background: tk.aiSoft, color: tk.blue700, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>Age {age}</span>}</div></div>
                  <div style={s.field}><label style={s.label}>Primary Phone</label><input style={s.input} value={form.primary_phone} onChange={e => set('primary_phone', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Primary Email</label><input style={s.input} value={form.primary_email} onChange={e => set('primary_email', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Mailing Street</label><input style={s.input} value={form.mailing_street} onChange={e => set('mailing_street', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>City</label><input style={s.input} value={form.city} onChange={e => set('city', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>State</label><input style={s.input} value={form.mailing_state} onChange={e => set('mailing_state', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Zip</label><input style={s.input} value={form.mailing_zip} onChange={e => set('mailing_zip', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Sex</label><select style={s.selectField} value={form.sex || ''} onChange={e => set('sex', e.target.value)}><option value="">Select...</option><option>Male</option><option>Female</option></select></div>
                  <div style={s.field}>
                    <label style={s.label}>Marital Status</label>
                    <select style={s.selectField} value={form.marital_status} onChange={e => set('marital_status', e.target.value)}>
                      <option value="">Select...</option>
                      {['Single', 'Married', 'Divorced', 'Separated', 'Widowed', 'Domestic Partnership'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div style={s.field}><label style={s.label}>Race</label><input style={s.input} value={form.race} onChange={e => set('race', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Ethnicity</label><input style={s.input} value={form.ethnicity} onChange={e => set('ethnicity', e.target.value)} /></div>
                </div>
                <div style={s.fieldFull}><label style={s.label}>Dietary Restrictions / Notes</label><input style={s.input} value={form.dietary} onChange={e => set('dietary', e.target.value)} /></div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.substances}>
                {form.substances.map((sub, i) => (
                  <div key={i} style={s.repeatRow}>
                    <button style={s.removeBtn} onClick={() => removeSubstance(i)}>{'\u2715'} Remove</button>
                    <div style={{ ...s.grid3, clear: 'both', paddingTop: '4px' }}>
                      <div style={s.field}>
                        <label style={s.label}>Substance</label>
                        <select style={s.selectField} value={sub.substance} onChange={e => updateSubstance(i, 'substance', e.target.value)}>
                          <option value="">Select...</option>
                          {sub.substance && !SUBSTANCES_LIST.includes(sub.substance) && <option value={sub.substance}>{sub.substance}</option>}
                          {SUBSTANCES_LIST.map(sl => <option key={sl}>{sl}</option>)}
                        </select>
                      </div>
                      {sub.substance === 'Other' && (
                        <div style={s.field}><label style={s.label}>Specify substance</label><input style={s.input} value={sub.details || ''} onChange={e => updateSubstance(i, 'details', e.target.value)} placeholder="e.g. Adderall, mushrooms" /></div>
                      )}
                      <div style={s.field}><label style={s.label}>Amount in 24hrs</label><input style={s.input} value={sub.amount_24h} onChange={e => updateSubstance(i, 'amount_24h', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Frequency</label><input style={s.input} value={sub.frequency} onChange={e => updateSubstance(i, 'frequency', e.target.value)} placeholder="Daily, weekly..." /></div>
                      <div style={s.field}>
                        <label style={s.label}>Administered</label>
                        <select style={s.selectField} value={sub.administered} onChange={e => updateSubstance(i, 'administered', e.target.value)}>
                          <option value="">Select...</option>
                          {ADMIN_OPTIONS.map(o => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div style={s.field}><label style={s.label}>Age of First Use</label><input style={s.input} value={sub.age_first_use} onChange={e => updateSubstance(i, 'age_first_use', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Last Use</label><input style={s.input} value={sub.last_use} onChange={e => updateSubstance(i, 'last_use', e.target.value)} placeholder="e.g. Yesterday" /></div>
                    </div>
                    <div style={s.grid2}>
                      <div style={s.field}><label style={s.label}>Is this a relapse?</label><YesNo value={sub.is_relapse} onChange={v => updateSubstance(i, 'is_relapse', v)} /></div>
                      <div style={s.field}><label style={s.label}>Length of relapse</label><input style={s.input} value={sub.relapse_length} onChange={e => updateSubstance(i, 'relapse_length', e.target.value)} /></div>
                    </div>
                    <div style={s.fieldFull}><label style={s.label}>Additional Notes</label><textarea style={s.textarea} value={sub.notes} onChange={e => updateSubstance(i, 'notes', e.target.value)} /></div>
                  </div>
                ))}
                <button style={s.addBtn} onClick={addSubstance}><Plus {...ICS} color={tk.ai} />Add Substance</button>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.treatment}>
                {form.treatment_history.map((t, i) => (
                  <div key={i} style={s.repeatRow}>
                    <button style={s.removeBtn} onClick={() => removeTreatment(i)}>{'\u2715'} Remove</button>
                    <div style={{ ...s.grid3, clear: 'both', paddingTop: '4px' }}>
                      <div style={s.field}><label style={s.label}>Facility Name</label><input style={s.input} value={t.facility_name} onChange={e => updateTreatment(i, 'facility_name', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Admission Date</label><input type="date" style={s.input} value={t.admission_date} onChange={e => updateTreatment(i, 'admission_date', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Discharge Date</label><input type="date" style={s.input} value={t.discharge_date} onChange={e => updateTreatment(i, 'discharge_date', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Levels of Care</label><input style={s.input} value={t.levels_of_care} onChange={e => updateTreatment(i, 'levels_of_care', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Location</label><input style={s.input} value={t.location} onChange={e => updateTreatment(i, 'location', e.target.value)} /></div>
                      <div style={s.field}>
                        <label style={s.label}>Completed?</label>
                        <select style={s.selectField} value={t.completed} onChange={e => updateTreatment(i, 'completed', e.target.value)}>
                          <option value="">Select...</option>
                          <option>Yes</option><option>No</option><option>AMA</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                <button style={s.addBtn} onClick={addTreatment}><Plus {...ICS} color={tk.ai} />Add Treatment History</button>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.mentalhealth}>
                <div style={s.fieldFull}><label style={s.label}>Any Mental Health Diagnosis?</label><textarea style={s.textarea} value={form.mh_diagnosis} onChange={e => set('mh_diagnosis', e.target.value)} /></div>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>Current SI/HI Ideation?</label><YesNo value={form.suicidal_homicidal_ideation} onChange={v => set('suicidal_homicidal_ideation', v)} /></div>
                  {form.suicidal_homicidal_ideation === 'Yes' && <div style={s.fieldFull}><label style={s.label}>SI/HI Details (thoughts, plan, toward whom?)</label><textarea style={s.textarea} value={form.si_hi_details} onChange={e => set('si_hi_details', e.target.value)} /></div>}
                  <div style={s.field}><label style={s.label}>Any Suicide Attempts?</label><YesNo value={form.suicide_attempts} onChange={v => set('suicide_attempts', v)} /></div>
                  {form.suicide_attempts === 'Yes' && <div style={s.fieldFull}><label style={s.label}>SI Attempt Notes (when, method, hospitalization?)</label><textarea style={s.textarea} value={form.si_attempt_details} onChange={e => set('si_attempt_details', e.target.value)} /></div>}
                </div>
                <div style={s.fieldFull}><label style={s.label}>Other Addictive Behaviors</label><input style={s.input} value={form.other_addictive_behaviors} onChange={e => set('other_addictive_behaviors', e.target.value)} /></div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.medical}>
                <div style={s.grid2}><div style={s.field}><label style={s.label}>Have you ever overdosed?</label><YesNo value={form.ever_overdosed} onChange={v => set('ever_overdosed', v)} /></div></div>
                {form.ever_overdosed === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Overdose Details</label><textarea style={s.textarea} value={form.overdose_details} onChange={e => set('overdose_details', e.target.value)} /></div>}
                <div style={s.fieldFull}><label style={s.label}>Medical Conditions / Health History</label><textarea style={s.textarea} value={form.medical_conditions} onChange={e => set('medical_conditions', e.target.value)} /></div>
                <div style={s.field}><label style={s.label}>History of Seizures?</label><YesNo value={form.seizure_history} onChange={v => set('seizure_history', v)} /></div>
                {form.seizure_history === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Seizure Details (when, cause, withdrawal-related?)</label><textarea style={s.textarea} value={form.seizure_details} onChange={e => set('seizure_details', e.target.value)} /></div>}
                <div style={s.field}><label style={s.label}>Eating Disorder?</label><YesNo value={form.eating_disorder} onChange={v => set('eating_disorder', v)} /></div>
                {form.eating_disorder === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Eating Disorder Details (type, history, treatment?)</label><textarea style={s.textarea} value={form.eating_disorder_details} onChange={e => set('eating_disorder_details', e.target.value)} /></div>}
                <div style={s.fieldFull}><label style={s.label}>Allergies (medications, foods {DASH} or "denies")</label><textarea style={{ ...s.textarea, minHeight: 50 }} value={form.allergies || ''} onChange={e => set('allergies', e.target.value)} /></div>
                <div style={{ ...s.label, marginBottom: '8px', display: 'block' }}>Current Medications</div>
                {form.current_medications.map((med, i) => (
                  <div key={i} style={s.repeatRow}>
                    <button style={s.removeBtn} onClick={() => removeMed(i)}>{'\u2715'}</button>
                    <div style={{ ...s.grid3, clear: 'both', paddingTop: '4px' }}>
                      <div style={s.field}><label style={s.label}>Medication</label><input style={s.input} value={med.medication} onChange={e => updateMed(i, 'medication', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Dosage</label><input style={s.input} value={med.dosage} onChange={e => updateMed(i, 'dosage', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Frequency</label><input style={s.input} value={med.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)} /></div>
                    </div>
                  </div>
                ))}
                <button style={s.addBtn} onClick={addMed}><Plus {...ICS} color={tk.ai} />Add Medication</button>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.functional}>
                <div style={s.fieldFull}><label style={s.label}>Contributing Factors</label><textarea style={s.textarea} value={form.contributing_factors} onChange={e => set('contributing_factors', e.target.value)} /></div>
                <div style={s.fieldFull}><label style={s.label}>Current Living Situation</label><input style={s.input} value={form.living_situation} onChange={e => set('living_situation', e.target.value)} /></div>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>Withdrawal Symptoms?</label><YesNo value={form.withdrawal_symptoms} onChange={v => set('withdrawal_symptoms', v)} /></div>
                  <div style={s.field}><label style={s.label}>AA or NA Involvement?</label><YesNo value={form.aa_na} onChange={v => set('aa_na', v)} /></div>
                </div>
                {form.withdrawal_symptoms === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Explain Withdrawal Symptoms</label><textarea style={s.textarea} value={form.withdrawal_details} onChange={e => set('withdrawal_details', e.target.value)} /></div>}
                <div style={s.fieldFull}><label style={s.label}>How do you support your use?</label><input style={s.input} value={form.how_support_use} onChange={e => set('how_support_use', e.target.value)} /></div>
                <div style={s.fieldFull}><label style={s.label}>Who initiated treatment and why?</label><textarea style={s.textarea} value={form.who_initiated} onChange={e => set('who_initiated', e.target.value)} /></div>
                <div style={s.fieldFull}><label style={s.label}>Family History of SA and MH Treatment</label><textarea style={s.textarea} value={form.family_history} onChange={e => set('family_history', e.target.value)} /></div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.legal}>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>Convicted of sexual crime?</label><YesNo value={form.sexual_crime_conviction} onChange={v => set('sexual_crime_conviction', v)} /></div>
                  <div style={s.field}><label style={s.label}>Convicted of arson?</label><YesNo value={form.arson_conviction} onChange={v => set('arson_conviction', v)} /></div>
                  <div style={s.field}><label style={s.label}>Any pending charges?</label><YesNo value={form.pending_charges} onChange={v => set('pending_charges', v)} /></div>
                  <div style={s.field}><label style={s.label}>Currently on probation?</label><YesNo value={form.on_probation} onChange={v => set('on_probation', v)} /></div>
                </div>
                {form.pending_charges === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Charges Details (include court)</label><textarea style={s.textarea} value={form.charges_details} onChange={e => set('charges_details', e.target.value)} /></div>}
                {form.on_probation === 'Yes' && <div style={s.fieldFull}><label style={s.label}>Probation Details</label><textarea style={s.textarea} value={form.probation_details} onChange={e => set('probation_details', e.target.value)} /></div>}
                <div style={s.fieldFull}><label style={s.label}>Legal History</label><textarea style={s.textarea} value={form.legal_history} onChange={e => set('legal_history', e.target.value)} /></div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.employment}>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>Currently employed?</label><YesNo value={form.currently_employed} onChange={v => set('currently_employed', v)} /></div>
                  <div style={s.field}><label style={s.label}>Employer knows about treatment?</label><YesNo value={form.employer_knows} onChange={v => set('employer_knows', v)} /></div>
                  <div style={s.field}><label style={s.label}>Insurance through employer?</label><YesNo value={form.insurance_through_employer} onChange={v => set('insurance_through_employer', v)} /></div>
                  <div style={s.field}><label style={s.label}>FMLA needed?</label><YesNo value={form.needs_fmla} onChange={v => set('needs_fmla', v)} /></div>
                </div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.contacts}>
                {form.related_contacts.map((c, i) => (
                  <div key={i} style={s.repeatRow}>
                    <button style={s.removeBtn} onClick={() => removeContact(i)}>{'\u2715'} Remove</button>
                    <div style={{ ...s.grid3, clear: 'both', paddingTop: '4px' }}>
                      <div style={s.field}><label style={s.label}>Name</label><input style={s.input} value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Relationship</label><input style={s.input} value={c.relationship} onChange={e => updateContact(i, 'relationship', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Primary Phone</label><input style={s.input} value={c.primary_phone} onChange={e => updateContact(i, 'primary_phone', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Secondary Phone</label><input style={s.input} value={c.secondary_phone} onChange={e => updateContact(i, 'secondary_phone', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>Email</label><input style={s.input} value={c.email} onChange={e => updateContact(i, 'email', e.target.value)} /></div>
                      <div style={s.field}><label style={s.label}>State</label><input style={s.input} value={c.state} onChange={e => updateContact(i, 'state', e.target.value)} /></div>
                    </div>
                    <div style={s.field}><label style={s.label}>Emergency Contact?</label><YesNo value={c.is_emergency} onChange={v => updateContact(i, 'is_emergency', v)} /></div>
                  </div>
                ))}
                <button style={s.addBtn} onClick={addContact}><Plus {...ICS} color={tk.ai} />Add Contact</button>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.mhtrack}>
                <div style={s.fieldFull}><label style={s.label}>What MH symptoms led you to seek help?</label><textarea style={s.textarea} value={form.mh_symptoms} onChange={e => set('mh_symptoms', e.target.value)} /></div>
                <div style={s.fieldFull}><label style={s.label}>Currently struggling with (MH)</label><textarea style={s.textarea} value={form.mh_current_struggles} onChange={e => set('mh_current_struggles', e.target.value)} /></div>
                <div style={s.grid2}>
                  <div style={s.field}><label style={s.label}>ADL Skills</label><input style={s.input} value={form.adl_skills} onChange={e => set('adl_skills', e.target.value)} /></div>
                  <div style={s.field}><label style={s.label}>Mania or Psychosis?</label><YesNo value={form.mania_psychosis} onChange={v => set('mania_psychosis', v)} /></div>
                  <div style={s.field}><label style={s.label}>Previous SI/HI?</label><YesNo value={form.past_si_hi} onChange={v => set('past_si_hi', v)} /></div>
                  <div style={s.field}><label style={s.label}>Past/Current Self Harm?</label><YesNo value={form.self_harm} onChange={v => set('self_harm', v)} /></div>
                  <div style={s.field}><label style={s.label}>On Disability or Gov Support?</label><YesNo value={form.on_disability} onChange={v => set('on_disability', v)} /></div>
                </div>
              </PSection>
              <PSection form={form} openSections={openSections} toggleSection={toggleSection} saveAndNext={saveAndNext} def={D.recommendation}>
                {[
                  { key: 'recommend_dtx', label: 'DTX ' + DASH + ' Detox / Residential', criteria: ['Client is free from self-harm and cleared to begin detox services', 'Biomedical conditions are stable or being addressed concurrently', 'Structured therapy and programmatic support are required'] },
                  { key: 'recommend_rtc', label: 'RTC ' + DASH + ' Residential Treatment', criteria: ['Client requires 24-hour structured care', 'Client is medically stable but requires intensive support', 'Environmental separation from substance use triggers is necessary'] },
                  { key: 'recommend_php', label: 'PHP ' + DASH + ' Partial Hospitalization', criteria: ['Client is medically and psychiatrically stable', 'Intensive daily programming is indicated', 'Client has stable housing and support'] },
                  { key: 'recommend_iop', label: 'IOP ' + DASH + ' Intensive Outpatient', criteria: ['Client is free from intoxication or withdrawal symptoms', 'Ongoing structured therapy and recovery support are indicated'] },
                  { key: 'recommend_op', label: 'OP ' + DASH + ' Outpatient / Individual Counseling', criteria: ['Client can maintain abstinence with minimal support', 'Scheduled counseling sessions support ongoing recovery goals'] },
                ].map(rec => (
                  <div key={rec.key} style={form[rec.key] ? s.recBoxActive : s.recBox}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form[rec.key]} onChange={e => set(rec.key, e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px' }} />
                      <div>
                        <div style={s.recTitle}>{rec.label}</div>
                        <div style={s.recCriteria}>{rec.criteria.map((c, i) => <div key={i}>{DOT} {c}</div>)}</div>
                      </div>
                    </label>
                  </div>
                ))}
                <div style={{ marginTop: '14px', padding: '15px', background: form.recommendation_to_admit === true ? tk.successBg : form.recommendation_to_admit === false ? tk.dangerBg : tk.surfaceSoft, border: `1.5px solid ${form.recommendation_to_admit === true ? tk.success : form.recommendation_to_admit === false ? tk.danger : tk.border}`, borderRadius: tk.radiusCard }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: tk.ink, marginBottom: '10px' }}>Recommendation to Admit this Client</div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => set('recommendation_to_admit', true)}
                      style={s.btn(form.recommendation_to_admit === true ? tk.success : tk.surface, form.recommendation_to_admit === true ? '#fff' : tk.success, tk.success)}>
                      <CheckCircle2 {...ICS} color={form.recommendation_to_admit === true ? '#fff' : tk.success} />Recommend Admit</button>
                    <button type="button" onClick={() => set('recommendation_to_admit', false)}
                      style={s.btn(form.recommendation_to_admit === false ? tk.danger : tk.surface, form.recommendation_to_admit === false ? '#fff' : tk.danger, tk.danger)}>
                      <X {...ICS} color={form.recommendation_to_admit === false ? '#fff' : tk.danger} />Do Not Admit</button>
                    {form.recommendation_to_admit !== null && form.recommendation_to_admit !== undefined && (
                      <button type="button" onClick={() => set('recommendation_to_admit', null)} style={s.btn(tk.surface, tk.textSecondary, tk.border)}>Clear</button>
                    )}
                  </div>
                  {(form.recommendation_to_admit === null || form.recommendation_to_admit === undefined) && (
                    <div style={{ marginTop: '8px', fontSize: '12.5px', color: tk.textSecondary }}>Not yet answered {DASH} select one before completing.</div>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  {(() => {
                    let a2 = null;
                    try { a2 = typeof form.asam_summary === 'string' ? JSON.parse(form.asam_summary) : form.asam_summary; } catch (e) { a2 = null; }
                    const doc = ai && !ai.empty && ai.asam ? ai.asam : a2;
                    if (!doc) return null;
                    const sevC = (n) => n == null ? tk.textMuted : n >= 3 ? tk.danger : n >= 2 ? tk.warning : tk.success;
                    const DIMS2 = ['Intoxication / Withdrawal', 'Biomedical Conditions', 'Emotional / Behavioral', 'Readiness to Change', 'Relapse Potential', 'Recovery Environment'];
                    return (
                      <div style={{ border: `1px solid ${tk.borderSoft}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14, background: '#FBFBFD' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>ASAM Six-Dimension Assessment</div>
                        {['d1','d2','d3','d4','d5','d6'].map((d, i) => (
                          <div key={d} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: tk.ink }}>
                              Dimension {i + 1}: {DIMS2[i]}
                              {doc[d]?.severity != null && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: sevC(doc[d].severity) }}>Severity {doc[d].severity}</span>}
                            </div>
                            <div style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.55, marginTop: 2, whiteSpace: 'pre-wrap' }}>{doc[d]?.narrative || 'No evidence documented.'}</div>
                          </div>
                        ))}
                        {doc.loc_recommendation && (
                          <div style={{ fontSize: 12.5, marginTop: 4 }}><b>Recommended Level of Care:</b> {doc.loc_recommendation}</div>
                        )}
                        {doc.medical_necessity_summary && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: tk.ink }}>Medical Necessity</div>
                            <div style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.55, marginTop: 2, whiteSpace: 'pre-wrap' }}>{doc.medical_necessity_summary}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <label style={s.label}>Additional Recommendation Notes</label>
                  <textarea style={{ ...s.textarea, marginTop: '4px', width: '100%', boxSizing: 'border-box' }} value={form.recommendation_notes} onChange={e => set('recommendation_notes', e.target.value)} placeholder="Any additional clinical notes..." />
                </div>
              </PSection>
            </>
          );
        })()}
        </div>

        {/* RIGHT RAIL */}
        <div style={{ position: isMobile ? 'static' : 'sticky', top: 90, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...s.card, padding: '13px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <CircleHelp {...ICS} color={tk.warning} />Information Still Needed
              </span>
              {ai && !ai.empty && (ai.gaps || []).length > 0 && <span style={s.chip(tk.warningBg, tk.warning)}>{ai.gaps.length} Questions</span>}
            </div>
            {ai && !ai.empty && (ai.gaps || []).length > 0 ? (
              <>
                {ai.gaps.map((g, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.5, padding: '5px 0', borderTop: i ? `1px solid ${tk.borderSoft}` : 'none' }}>
                    {g.question}
                    {g.why && <div style={{ fontSize: 11, color: tk.textMuted }}>{g.why}</div>}
                  </div>
                ))}
                {selectedLead?.phone && (
                  <a href={`tel:${selectedLead.phone}`} style={{ ...s.btn(tk.surface, tk.ai, tk.ai), width: '100%', boxSizing: 'border-box', justifyContent: 'center', marginTop: 10, textDecoration: 'none' }}>
                    <PhoneCall {...ICS} color={tk.ai} />Start Callback
                  </a>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: tk.textMuted }}>Run the Prescreen Assistant to identify callback questions from this lead{'\u2019'}s calls.</div>
            )}
          </div>
          <div style={{ ...s.card, padding: '13px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <ClipboardCheck {...ICS} color={tk.teal} />ASAM Placement Analysis
              </span>
            </div>
            {(() => {
              let a = null;
              try { a = typeof form.asam_summary === 'string' ? JSON.parse(form.asam_summary) : form.asam_summary; } catch (e) { a = null; }
              const live = ai && !ai.empty && ai.asam ? ai.asam : a;
              if (!live) return <div style={{ fontSize: 12, color: tk.textMuted }}>Not ready {DASH} {missing.length} required field{missing.length === 1 ? '' : 's'} remain. Run the Assistant once calls are in.</div>;
              const sevColor = (n) => n == null ? tk.textMuted : n >= 3 ? tk.danger : n >= 2 ? tk.warning : tk.success;
              const DIMS = ['Intoxication / Withdrawal', 'Biomedical Conditions', 'Emotional / Behavioral', 'Readiness to Change', 'Relapse Potential', 'Recovery Environment'];
              return (
                <>
                  {['d1','d2','d3','d4','d5','d6'].map((d, i) => (
                    <div key={d} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderTop: i ? `1px solid ${tk.borderSoft}` : 'none', fontSize: 12 }}>
                      <span style={{ color: tk.ink }} title={live[d]?.narrative || ''}><b style={{ color: tk.textMuted }}>D{i + 1}</b> {DIMS[i]}</span>
                      <span style={{ fontWeight: 700, color: sevColor(live[d]?.severity), fontVariantNumeric: 'tabular-nums' }}>{live[d]?.severity ?? DASH}</span>
                    </div>
                  ))}
                  {live.loc_recommendation && (
                    <div style={{ marginTop: 8, background: tk.purpleSoft, border: `1px solid ${tk.purple}`, borderRadius: tk.radiusControl, padding: '8px 11px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: tk.purple, textTransform: 'uppercase', letterSpacing: '0.06em' }}><Sparkles {...ICS} color={tk.purple} />TCIO Recommendation</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: tk.ink, marginTop: 2 }}>{live.loc_recommendation}</div>
                      <div style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 2 }}>TCIO recommends {DASH} the clinician decides in Recommendation to Admit.</div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* STICKY BOTTOM ACTION BAR */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: tk.surface, borderTop: `1px solid ${tk.borderSoft}`, boxShadow: '0 -4px 16px rgba(10,34,57,0.08)', padding: '10px 24px', zIndex: 100 }}>
        <div style={{ maxWidth: 1360, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <ProgressBar width={isMobile ? 130 : 180} />
            <AutoPill />
            {!allComplete && (
              <span style={{ fontSize: 11.5, color: tk.warning, fontWeight: 600 }}>
                Missing: {missing.slice(0, 3).map(([, l]) => l).join(', ')}{missing.length > 3 ? ` +${missing.length - 3} more` : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {saveMsg && <span style={saveMsg.type === 'success' ? s.successMsg : s.errorMsg}>{saveMsg.text}</span>}
            <button style={s.btn(tk.surface, tk.textSecondary, tk.border)} onClick={() => handleSave('draft')} disabled={saving}>Save Draft</button>
            <button style={s.btn(tk.warningBg, tk.warning, tk.warning)} onClick={() => handleSave('pending_dr_approval')} disabled={saving}>Pending Dr Approval</button>
            <button style={{ ...s.btn(tk.surface, tk.purple, tk.purple), opacity: (!existingPrescreen || sending) ? 0.5 : 1 }} onClick={handleSendEmail} disabled={!existingPrescreen || sending}>
              <Mail {...ICS} color={tk.purple} />{sending ? 'Sending' + ELLIP : 'Send Email'}</button>
            <button style={{ ...s.btn(tk.surface, tk.teal, tk.teal), opacity: !existingPrescreen ? 0.5 : 1 }} onClick={handlePrint} disabled={!existingPrescreen}>
              <Download {...ICS} color={tk.teal} />PDF</button>
            {!kipuHiddenFor(selectedLead?.facility_id) && (
              <button style={{ ...s.btn(kipuPush[existingPrescreen?.id] === 'done' ? tk.successBg : tk.purpleSoft, kipuPush[existingPrescreen?.id] === 'done' ? tk.success : tk.purple, kipuPush[existingPrescreen?.id] === 'done' ? tk.success : tk.purple), opacity: (!existingPrescreen || kipuPush[existingPrescreen?.id] === 'sending') ? 0.5 : 1 }}
                onClick={() => existingPrescreen && handleKipuPush(existingPrescreen.id)} disabled={!existingPrescreen || kipuPush[existingPrescreen?.id] === 'sending'}>
                {existingPrescreen && kipuPush[existingPrescreen.id] === 'sending' ? 'Sending' + ELLIP : existingPrescreen && kipuPush[existingPrescreen.id] === 'done' ? CHECK + ' Kipu' : ARROW + ' Kipu'}
              </button>
            )}
            <button style={{ ...s.btn(allComplete ? tk.ai : tk.surfaceSoft, allComplete ? '#fff' : tk.textMuted, allComplete ? undefined : tk.border), cursor: allComplete ? 'pointer' : 'not-allowed' }}
              onClick={() => allComplete && handleSave('complete')} disabled={saving || !allComplete}
              title={allComplete ? '' : 'Missing: ' + missing.map(([, l]) => l).join(', ')}>
              {saving ? 'Saving' + ELLIP : CHECK + ' Complete Pre-Assessment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
